/**
 * ReportIssueModal — lets any crew submit a Bug / Feature / Question / Other
 * report. Auto-stamps the reporter's name + role + store on the backend so
 * users don't have to type it. Sends to:
 *   1. db.bug_reports (admin triage list)
 *   2. Each admin's inbox (in-app)
 *   3. Each admin's notification_email via Resend
 * (User ask 2026-05-21.)
 */
import { useState } from "react";
import axios from "axios";
import { X, Bug, Sparkle, Question, NoteBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "bug",      label: "Bug",      icon: Bug,       color: "rose"    },
  { key: "feature",  label: "Feature",  icon: Sparkle,   color: "yellow"  },
  { key: "question", label: "Question", icon: Question,  color: "blue"    },
  { key: "other",    label: "Other",    icon: NoteBlank, color: "slate"   },
];

const COLOR_CLASSES = {
  rose:   { active: "bg-rose-100 border-rose-400 text-rose-700",     idle: "border-slate-200 text-slate-500 hover:border-rose-300" },
  yellow: { active: "bg-yellow-100 border-yellow-400 text-yellow-700", idle: "border-slate-200 text-slate-500 hover:border-yellow-300" },
  blue:   { active: "bg-blue-100 border-blue-400 text-blue-700",      idle: "border-slate-200 text-slate-500 hover:border-blue-300" },
  slate:  { active: "bg-slate-200 border-slate-400 text-slate-700",   idle: "border-slate-200 text-slate-500 hover:border-slate-300" },
};

export default function ReportIssueModal({ user, onClose, currentPage = "" }) {
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy || !message.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/bug-reports`, {
        reporter_id: user?.crew_id,
        category,
        message: message.trim(),
        page: currentPage || (typeof window !== "undefined" ? window.location.pathname : ""),
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      });
      toast.success("Thanks! Your report's been sent.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send. Try again?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[75] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="report-issue-modal"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 flex items-start justify-between gap-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold text-white/80">Help us improve</p>
            <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Report an issue</h2>
            <p className="text-xs text-white/80 mt-0.5">Goes straight to Dom · auto-stamps your name + store</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1 -mr-1" aria-label="Close" data-testid="report-issue-close">
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          {/* Category picker */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Category</p>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const cls = COLOR_CLASSES[c.color];
                const active = category === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    className={`rounded-lg border-2 px-2 py-2.5 flex flex-col items-center gap-1 text-[11px] font-bold uppercase tracking-wider transition-all ${active ? cls.active : cls.idle}`}
                    data-testid={`report-cat-${c.key}`}
                  >
                    <Icon weight="bold" className="w-4 h-4" />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">What's going on?</p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={category === "bug" ? "Tell me what broke + how to reproduce it…" : category === "feature" ? "What should the app do?" : category === "question" ? "What's confusing or missing?" : "Anything else on your mind?"}
              rows={5}
              className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 resize-none"
              data-testid="report-message"
              maxLength={2000}
            />
            <p className="text-[10px] text-slate-400 mt-1 text-right">{message.length}/2000</p>
          </div>

          {/* Auto-attached metadata preview */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Auto-attached</p>
            <p className="text-xs text-slate-600">
              <span className="font-bold">{user?.first_name || "You"}</span>
              {user?.crew_id ? ` · ID ${user.employee_id || user.crew_id}` : ""}
              {user?.is_district_manager ? " · District Manager" : user?.home_store_code ? ` · Store ${user.home_store_code}` : ""}
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="report-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !message.trim()}
            style={{ backgroundColor: "var(--fb-blue)" }}
            data-testid="report-submit"
          >
            {busy ? "Sending…" : "Send report"}
          </Button>
        </div>
      </div>
    </div>
  );
}
